import * as React from 'react'
import { useState, useRef } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { snack } from '../../../../utils/SnackbarUtilsConfigurator'
import CreditCardElement from '../../../../components/shared/credit-card-element'
import { Add_Payment_Method } from '../../../../helper/api'

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      padding: '20px 4px',
      height: '39px',
      backgroundColor: 'white',
      iconColor: '#8898AA',
      color: 'black',
      fontWeight: 300,
      fontSize: '16px',
      '::placeholder': { color: '#a9b7c4' },
    },
    invalid: {
      color: '#e85746',
    },
  },
  classes: {
    focus: 'is-focused',
    empty: 'is-empty',
  },
}

const CreditCardErrors = ({ children }) => (
  <div
    className="cardErrors"
    role="alert"
    style={{ color: '#fa755a', fontSize: '16px', marginTop: '4px' }}
  >
    {children}
  </div>
)

function App({ setupIntentSecret, successUrl, isCreditCardStored, clientSecret, onClose }) {
  const formRef = useRef(null)
  const [cardErrorMessage, setCardErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [cardReady, setCardReady] = useState(false)
  const [cardDetail, setCardDetail] = useState(null)
  const [formComplete, setFormComplete] = useState(false)
  const [stripePaymentMethodId, setStripePaymentMethodId] = useState('')
  const [isStripeFormVisible, setIsStripeFormVisible] = useState(!isCreditCardStored)

  const stripe = useStripe()
  const elements = useElements()
  const customerId = useSelector((state) => state.NewOrder.newOrder.customer.id)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const toggleVisibility = () => setIsStripeFormVisible((prev) => !prev)

  /** -------------------------------
   * ✅ HANDLE SUBMIT (MAIN FUNCTION)
   --------------------------------*/
  const handleSubmit = React.useCallback(
    async (event) => {
      event.preventDefault()
      console.group('[StripeDebug] handleSubmit triggered')

      console.log('[StripeDebug] Initial state:', {
        customerId,
        cardDetail,
        stripeDefined: !!stripe,
        elementsDefined: !!elements,
        clientSecret,
        stripeMoto: global?.config?.dispatcherSettings?.stripeMoto,
        timestamp: new Date().toISOString(),
      })

      try {
        setLoading(true)
        setCardErrorMessage(null)

        // -----------------------------
        // 🧩 1. حالت MOTO (ورود دستی کارت)
        // -----------------------------
        if (global?.config?.dispatcherSettings?.stripeMoto) {
          console.log('[StripeDebug] Mode: Stripe MOTO (manual card entry)')

          if (!cardDetail) {
            console.error('[StripeDebug] ❌ Missing card details')
            snack.error('Card details are missing')
            setLoading(false)
            console.groupEnd()
            return
          }

          const payload = {
            customerId,
            cardExpMonth: String(cardDetail.expired).substring(0, 2),
            cardExpYear: `20${String(cardDetail.expired).substring(2)}`,
            cardNumber: cardDetail.cardNumber,
            cardCvc: cardDetail.cvc,
          }

          console.log('[StripeDebug] Sending payload to Add_Payment_Method:', payload)

          Add_Payment_Method(payload, (call) => {
            console.log('[StripeDebug] Add_Payment_Method callback result:', call)
            if (call) {
              snack.success_i18('Addedsuccessfully')
              onClose(true)
            } else {
              snack.error('Failed to add payment method')
            }
            setLoading(false)
            console.groupEnd()
          })

          return
        }

        // -----------------------------
        // 💳 2. حالت Stripe Elements
        // -----------------------------
        console.log('[StripeDebug] Mode: Stripe Elements')
        setFormComplete(false)

        if (!stripe || !elements) {
          console.error('[StripeDebug] ❌ Stripe or Elements not initialized properly')
          snack.error('Stripe is not initialized properly.')
          setLoading(false)
          console.groupEnd()
          return
        }

        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
          console.error('[StripeDebug] ❌ CardElement not found')
          snack.error('Card element is missing.')
          setLoading(false)
          console.groupEnd()
          return
        }

        if (!clientSecret) {
          console.error('[StripeDebug] ❌ Missing clientSecret for setup')
          snack.error('Missing client secret for Stripe setup.')
          setLoading(false)
          console.groupEnd()
          return
        }

        console.log('[StripeDebug] Calling stripe.confirmCardSetup...')
        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement },
        })

        console.log('[StripeDebug] Stripe confirmCardSetup result:', result)

        if (result.error) {
          console.error('[StripeDebug] ❌ Stripe Error:', result.error)
          setCardErrorMessage(result.error.message || 'Card setup failed')
          snack.error(result.error.message || 'Card setup failed')
          setLoading(false)
          console.groupEnd()
          return
        }

        if (result.setupIntent?.status === 'succeeded') {
          console.log('[StripeDebug] ✅ Card setup succeeded:', result.setupIntent)
          snack.success_i18('Addedsuccessfully')
          setLoading(false)
          onClose(true)
          console.groupEnd()
          return
        }

        console.warn('[StripeDebug] ⚠️ Unexpected Stripe response:', result)
        snack.error('Unexpected response from Stripe')
        setLoading(false)
        console.groupEnd()
      } catch (err) {
        console.error('[StripeDebug] 💥 Unexpected error in handleSubmit:', err)
        snack.error('An unexpected error occurred')
        setLoading(false)
        console.groupEnd()
      }
    },
    [customerId, cardDetail, stripe, elements, clientSecret, onClose]
  )

  /** -------------------------------
   * 🧠 CARD ELEMENT VALIDATION
   --------------------------------*/
  const validateCardElement = (event) => {
    console.log('[StripeDebug] CardElement change event:', event)
    setFormComplete(event.complete)
    setCardErrorMessage(event.error ? event.error.message : null)
  }

  /** -------------------------------
   * 💳 HANDLE MANUAL CREDIT CARD (MOTO)
   --------------------------------*/
  const handleCreditCard = (isOk, data) => {
    console.log('[StripeDebug] handleCreditCard:', { isOk, data })
    if (isOk) {
      setCardReady(true)
      setCardDetail(data)
    } else {
      setCardReady(false)
      setCardDetail(null)
    }
  }

  return (
    <React.Fragment>
      <div
        style={{
          width: '511px',
          margin: '24px 0 21px 16px',
          float: 'left',
        }}
      >
        {global?.config?.dispatcherSettings?.stripeMoto ? (
          <CreditCardElement onChange={handleCreditCard} />
        ) : (
          <>
            <fieldset className={`feohebxte ${cardErrorMessage ? 'feohebxteError' : ''}`}>
              <CardElement
                options={CARD_OPTIONS}
                className="col-md-12"
                onChange={validateCardElement}
              />
            </fieldset>
            {cardErrorMessage && <CreditCardErrors>{cardErrorMessage}</CreditCardErrors>}
          </>
        )}
      </div>

      <div className="footer">
        <sendOrder
          className={`creditcardaddBtn ${
            global?.config?.dispatcherSettings?.stripeMoto
              ? !cardReady && 'grey'
              : !formComplete && 'grey'
          }`}
          onClick={(cardReady || formComplete) && handleSubmit}
        >
          {loading ? (
            <div className="snippet" data-title=".dot-pulse">
              <div className="stage">
                <div className="dot-pulse"></div>
              </div>
            </div>
          ) : (
            <Trans i18nKey={'Add'}> </Trans>
          )}
        </sendOrder>
      </div>
    </React.Fragment>
  )
}

export default App
